import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getRack } from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation, Tabs } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import { Edit } from 'lucide-react'
import useTabs from '@/hooks/useTabs'
import { formatDateTime, getRackStatusBadgeVariant } from '../../utils'
import { tabs } from './tabs'

const RackDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const { data: rack, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, id],
        queryFn: () => getRack(id!),
    })

    const { TabComponent, TabProps } = useTabs({ tabs: tabs() })

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div>
            <SubHeaders
                actions={[
                    {
                        icon: <Edit className="w-4 h-4" />,
                        label: 'Edit',
                        onClick: () =>
                            navigate(`${paths.warehouseRacks}/${id}/edit`),
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: paths.warehouseRacks,
                        title: 'Racks',
                    },
                    {
                        path: '',
                        title: rack?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Rack Code',
                        value: rack?.code || '-',
                    },
                    {
                        label: 'Rack Name',
                        value: rack?.name || '-',
                    },
                    {
                        label: 'Location',
                        value: rack?.location?.name || '-',
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={getRackStatusBadgeVariant(
                                    rack?.status || ''
                                )}
                            >
                                {rack?.status?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'U Height',
                        value: rack?.u_height ? `${rack.u_height}U` : '-',
                    },
                    {
                        label: 'Power Supply',
                        value: rack?.power_supply
                            ? `${rack.power_supply.toLocaleString()} W`
                            : '-',
                        hidden: !rack?.power_supply,
                    },
                    {
                        label: 'Bandwidth Limit',
                        value: rack?.bandwidth_limit
                            ? `${rack.bandwidth_limit.toLocaleString()} Mbps`
                            : '-',
                        hidden: !rack?.bandwidth_limit,
                    },
                    {
                        label: 'Bandwidth Usage',
                        value: rack?.bandwidth_reality
                            ? `${rack.bandwidth_reality.toLocaleString()} Mbps`
                            : '-',
                        hidden: !rack?.bandwidth_reality,
                    },
                    {
                        label: 'Started At',
                        value: rack?.started_at
                            ? formatDateTime(rack.started_at)
                            : '-',
                        hidden: !rack?.started_at,
                    },
                    {
                        label: 'Cancelled At',
                        value: rack?.cancelled_at
                            ? formatDateTime(rack.cancelled_at)
                            : '-',
                        hidden: !rack?.cancelled_at,
                    },
                    {
                        label: 'Description',
                        value: rack?.description || '-',
                        className: 'lg:col-span-2',
                        hidden: !rack?.description,
                    },
                    {
                        label: 'Notes',
                        value: rack?.notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !rack?.notes,
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(rack?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(rack?.updated_at),
                    },
                ]}
                head={{
                    title: 'Rack Information',
                }}
            />

            <Tabs {...TabProps} />

            {TabComponent}
        </div>
    )
}

export default RackDetail
